import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Calendar, Plus, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHODS = ['cash', 'card', 'stripe', 'venmo', 'cashapp', 'other'];

const Revenue = () => {
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: '', service: '', amount: '', method: 'cash', note: '' });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API_URL}/api/revenue/summary`),
        axios.get(`${API_URL}/api/payments`),
      ]);
      setSummary(s.data);
      setPayments(p.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.service || !form.amount) return;
    await axios.post(`${API_URL}/api/payments`, {
      client_name: form.client_name,
      service: form.service,
      amount: parseFloat(form.amount),
      method: form.method,
      note: form.note || null,
    });
    setForm({ client_name: '', service: '', amount: '', method: 'cash', note: '' });
    setShowAdd(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    await axios.delete(`${API_URL}/api/payments/${id}`);
    load();
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading revenue...</div>;

  const stats = [
    { label: 'Today', val: summary?.today || 0, icon: DollarSign, color: 'text-emerald-500' },
    { label: 'This Week', val: summary?.week || 0, icon: TrendingUp, color: 'text-blue-500' },
    { label: 'This Month', val: summary?.month || 0, icon: Calendar, color: 'text-amber-500' },
    { label: 'This Year', val: summary?.year || 0, icon: TrendingUp, color: 'text-pink-500' },
  ];

  return (
    <div className="space-y-6" data-testid="revenue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Revenue</h1>
          <p className="text-sm text-muted-foreground mt-1">Track every dollar that comes in.</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="revenue-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="revenue-stats">
        {stats.map(s => (
          <Card key={s.label} className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div className="text-3xl font-semibold">${s.val.toFixed(2)}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">All-Time Revenue</h2>
          <span className="text-2xl font-bold text-amber-600">${(summary?.all_time || 0).toFixed(2)}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{summary?.payment_count || 0} total payments recorded</p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold mb-3">Top Services</h3>
          {summary?.top_services?.length ? (
            <ul className="space-y-2">
              {summary.top_services.map((s, i) => (
                <li key={i} className="flex justify-between text-sm" data-testid={`top-service-${i}`}>
                  <span>{s.service}</span>
                  <span className="font-medium text-emerald-600">${s.total.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">No payments yet.</p>}
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold mb-3">Top Clients</h3>
          {summary?.top_clients?.length ? (
            <ul className="space-y-2">
              {summary.top_clients.map((c, i) => (
                <li key={i} className="flex justify-between text-sm" data-testid={`top-client-${i}`}>
                  <span>{c.client}</span>
                  <span className="font-medium text-emerald-600">${c.total.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">No payments yet.</p>}
        </Card>
      </div>

      {/* Payments table */}
      <Card className="p-5">
        <h3 className="font-semibold mb-3">Recent Payments</h3>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments recorded yet. Click "Record Payment" to add one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Client</th>
                  <th className="py-2 pr-4">Service</th>
                  <th className="py-2 pr-4">Method</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-b last:border-b-0" data-testid={`payment-row-${p.id}`}>
                    <td className="py-2 pr-4">{new Date(p.paid_at).toLocaleDateString()}</td>
                    <td className="py-2 pr-4">{p.client_name}</td>
                    <td className="py-2 pr-4">{p.service}</td>
                    <td className="py-2 pr-4 capitalize">{p.method}</td>
                    <td className="py-2 pr-4 text-right font-medium text-emerald-600">${p.amount.toFixed(2)}</td>
                    <td className="py-2 pr-2 text-right">
                      <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <Card className="p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="payment-form">
            <h3 className="font-semibold text-lg mb-4">Record Payment</h3>
            <form onSubmit={submit} className="space-y-3">
              <Input
                placeholder="Client name"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                required
                data-testid="payment-client"
              />
              <Input
                placeholder="Service (e.g., Acrylic Full Set)"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                required
                data-testid="payment-service"
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Amount ($)"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
                data-testid="payment-amount"
              />
              <select
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="w-full border rounded-md px-3 py-2 bg-background"
                data-testid="payment-method"
              >
                {METHODS.map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
              </select>
              <Input
                placeholder="Note (optional)"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="payment-note"
              />
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1" data-testid="payment-submit">Save</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Revenue;
